// components/formDialog/TechProgramDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateTechProgramMutation,
  useUpdateTechProgramMutation,
} from "@/hooks/api/techProgram.queries";
import { batchService } from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import { studentService } from "@/services/student.service";
import {
  ProgramStatus,
  TechProgramCreateType,
  TechProgramParamsType,
  TechProgramType,
  techProgramService,
} from "@/services/techProgram.service";
import { techSkillVendorService } from "@/services/techSkillVendor.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type TechProgramDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TechProgramParamsType;
  programId?: string;
  programData?: TechProgramType | null;
  onCancel: () => void;
};

const STATUS_OPTIONS: { label: string; value: ProgramStatus }[] = [
  { label: "Upcoming", value: "upcoming" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
];

const MODE_OPTIONS = [
  { label: "On-site", value: "On-site" },
  { label: "Online", value: "Online" },
  { label: "Hybrid", value: "Hybrid" },
];

const techProgramSchema = z.object({
  name: z.string().min(1, "Program Name is required"),
  vendor: z.string().min(1, "Vendor/Trainer is required"),
  technologies: z
    .array(z.string())
    .min(1, "At least one technology is required"),
  students: z.array(z.string()).optional(),
  departments: z.array(z.string()).optional(),
  batches: z.array(z.string()).optional(),
  duration: z.string().min(1, "Duration is required"),
  maxParticipants: z.number().optional(),
  mode: z.enum(["On-site", "Online", "Hybrid"]),
  startDate: z.string().min(1, "Start Date is required"),
  endDate: z.string().optional(),
  status: z.enum(["upcoming", "ongoing", "completed"]),
  prerequisites: z.string().optional(),
  programObjectives: z.string().optional(),
  programDescription: z.string().optional(),
});

type TechProgramFormType = z.infer<typeof techProgramSchema>;

const TechProgramDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  programId,
  programData,
  onCancel,
}: TechProgramDialogProps) => {
  const createMutation = useCreateTechProgramMutation(filters);
  const updateMutation = useUpdateTechProgramMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<TechProgramFormType>({
    resolver: zodResolver(techProgramSchema),
    defaultValues: {
      name: "",
      vendor: "",
      technologies: [],
      students: [],
      departments: [],
      batches: [],
      duration: "",
      maxParticipants: 0,
      mode: "On-site",
      startDate: "",
      endDate: "",
      status: "upcoming",
      prerequisites: "",
      programObjectives: "",
      programDescription: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && programData) {
        setTimeout(() => {
          form.reset({
            name: programData.name,
            vendor: programData.vendor,
            technologies: programData.technologies || [],
            students: programData.students || [],
            departments: programData.departments || [],
            batches: programData.batches || [],
            duration: programData.duration || "",
            maxParticipants: programData.maxParticipants || 0,
            mode: programData.mode || "On-site",
            startDate: programData.startDate || "",
            endDate: programData.endDate || "",
            status: programData.status || "upcoming",
            prerequisites: programData.prerequisites || "",
            programObjectives: programData.programObjectives || "",
            programDescription: programData.programDescription || "",
          });
        }, 0);
      } else {
        form.reset({
          name: "",
          vendor: "",
          technologies: [],
          students: [],
          departments: [],
          batches: [],
          duration: "",
          maxParticipants: 0,
          mode: "On-site",
          startDate: "",
          endDate: "",
          status: "upcoming",
          prerequisites: "",
          programObjectives: "",
          programDescription: "",
        });
      }
    }
  }, [mode, programData, isOpen, form]);

  const handleSubmit = (values: TechProgramFormType) => {
    const submissionData: TechProgramCreateType = {
      ...values,
      students: values.students || [],
      departments: values.departments || [],
      batches: values.batches || [],
      maxParticipants: values.maxParticipants || 0,
      endDate: values.endDate || "",
      prerequisites: values.prerequisites || "",
      programObjectives: values.programObjectives || "",
      programDescription: values.programDescription || "",
      enrolled: programData?.enrolled || 0, // Fallback preserving original values
      completed: programData?.completed || 0,
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && programId) {
      updateMutation.mutate(
        { _id: programId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultVendorOptions = programData?.vendor
    ? [
        {
          label: `Vendor ${programData.vendor}`, // Fallback if data not fully joined
          value: programData.vendor,
        },
      ]
    : [];

  const defaultTechnologiesOptions =
    programData?.technologies?.map((t) => ({
      label: t,
      value: t,
    })) || [];

  const defaultStudentOptions =
    programData?.students?.map((id) => ({
      label: `Student ${id}`,
      value: id,
    })) || [];

  const defaultDepartmentOptions =
    programData?.departments?.map((id) => ({
      label: `Dept ${id}`,
      value: id,
    })) || [];

  const defaultBatchOptions =
    programData?.batches?.map((id) => ({
      label: `Batch ${id}`,
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Tech Skills Program"
              : "Edit Tech Skills Program"}
          </DialogTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Fill in the details to{" "}
            {mode === "create" ? "add a new" : "edit the"} tech skills program
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Program Name */}
            <InputField<TechProgramFormType>
              type="text"
              name="name"
              label="Program Name"
              placeholder="e.g., Full Stack Web Development"
              required
              disabled={isPending}
            />

            {/* Vendor/Trainer */}
            <AsyncSingleSelectField<TechProgramFormType>
              name="vendor"
              label="Vendor/Trainer"
              placeholder="Select vendor"
              fetchOptions={techProgramService.fetchTechSkillVendorOptions}
              queryKey={["techSkillVendorsDrop"]}
              defaultOptions={defaultVendorOptions}
              required
              disabled={isPending}
            />

            {/* Technologies/Tools Covered */}
            <div className="md:col-span-2">
              <AsyncMultiSelectField<TechProgramFormType>
                name="technologies"
                label="Technologies/Tools Covered"
                placeholder="Select technologies"
                fetchOptions={techSkillVendorService.fetchTechnologiesOptions}
                queryKey={["technologies"]}
                defaultOptions={defaultTechnologiesOptions}
                required
                disabled={isPending}
              />
            </div>

            {/* Enrolled Students */}
            <div className="md:col-span-2">
              <AsyncMultiSelectField<TechProgramFormType>
                name="students"
                label="Enrolled Students"
                placeholder="Select students for this program"
                fetchOptions={studentService.fetchStudentOptions}
                queryKey={["students"]}
                defaultOptions={defaultStudentOptions}
                disabled={isPending}
              />
            </div>

            {/* Target Departments */}
            <AsyncMultiSelectField<TechProgramFormType>
              name="departments"
              label="Target Departments"
              placeholder="Select departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
            />

            {/* Target Batches */}
            <AsyncMultiSelectField<TechProgramFormType>
              name="batches"
              label="Target Batches"
              placeholder="Select batches"
              fetchOptions={batchService.fetchBatchOptions}
              queryKey={["batches"]}
              defaultOptions={defaultBatchOptions}
              disabled={isPending}
            />

            {/* 3-column Grid for Duration, Max Participants, Mode */}
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
              <InputField<TechProgramFormType>
                type="text"
                name="duration"
                label="Duration"
                placeholder="e.g., 3 months"
                required
                disabled={isPending}
              />

              <InputField<TechProgramFormType>
                type="number"
                name="maxParticipants"
                label="Max Participants"
                placeholder="e.g., 75"
                disabled={isPending}
              />

              <SelectField<TechProgramFormType>
                name="mode"
                label="Mode"
                options={MODE_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* 3-column Grid for Start Date, End Date, Status */}
            <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
              <InputField<TechProgramFormType>
                type="date"
                name="startDate"
                label="Start Date"
                required
                disabled={isPending}
              />

              <InputField<TechProgramFormType>
                type="date"
                name="endDate"
                label="End Date"
                disabled={isPending}
              />

              <SelectField<TechProgramFormType>
                name="status"
                label="Status"
                options={STATUS_OPTIONS}
                required
                disabled={isPending}
              />
            </div>

            {/* Prerequisites */}
            <TextareaField<TechProgramFormType>
              name="prerequisites"
              label="Prerequisites"
              placeholder="Required skills or knowledge before joining"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Program Objectives */}
            <TextareaField<TechProgramFormType>
              name="programObjectives"
              label="Program Objectives"
              placeholder="Learning objectives and goals"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Program Description */}
            <TextareaField<TechProgramFormType>
              name="programDescription"
              label="Program Description"
              placeholder="Brief description about the program"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="mt-4 md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Creating..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Program"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TechProgramDialog;
